/*============================================================================
  hbx-gap-closer.js — Hobolax 10-18 (Rob, topic 70: "add this much more and get
  free shipping ... maybe a popup at checkout").

  1. Gap-aware drawer upsell. Under the free-shipping tier the "Complete the look"
     row becomes "Add one → FREE shipping": in-stock best sellers priced >= the gap,
     youth/adult matched to the lead item, nothing already in the cart. Between
     the shipping tier and the next (10% off) tier it does the same for that tier.
     If nothing single closes the gap it shows the closest. At/above the top tier
     it hands back to the normal recommendations (returns false).
  2. Checkout nudge. Under the shipping tier, the FIRST checkout tap of the session
     opens a slide-up sheet with 2 one-tap adds and a big "Checkout anyway".
     It never blocks: no pool / no candidates / any error -> checkout proceeds.
     Express buttons and the Redo opt-out link are left alone.

  3. Tracking (Rob 10-20, Website edits). A hidden _hbx_gap cart attribute rides
     through to the order and logs the journey, "|"-separated:
       d:<variant> drawer add (dn: = next-reward tier)   s:<variant> sheet add
       n:<gap $> sheet shown   back = closed the sheet (X / scrim)   go = "Checkout anyway"
     GA4 gets hbx_gap_* events so shoppers who never buy are counted too, incl.
     hbx_gap_back_more / _same on the checkout tap after a back (cart grew or not).

  cart-extras.js calls HbxGap.handle(cart, wrap, utils) from loadUpsell, so this
  file must load BEFORE cart-extras.js. Pool: sections/hbx-gap-pool.liquid.
============================================================================*/
(function () {
  'use strict';

  var APPAREL = /t-shirt|tee|hoodie|crewneck|jogger|shorts|sweat|jersey|tank|pinnie/i;
  var NUDGE_KEY = 'hbxGapNudgeShown';
  var pool = null, poolPromise = null;
  var utils = null, lastCart = null, lastState = null;

  /* ---- 3. Tracking ---- */
  var LOG_ATTR = '_hbx_gap', BACK_KEY = 'hbxGapBack';
  var gapLog = null, logWrite = Promise.resolve(), writing = 0, releasing = false;

  function ga(name, params) {
    try { if (typeof window.gtag === 'function') window.gtag('event', name, params || {}); } catch (e) {}
  }

  /* Append a token to the cart attribute. Writes are queued so they never race each other. */
  function logEvent(tok) {
    writing++;
    logWrite = logWrite.then(function () {
      var cur = gapLog != null ? gapLog : ((lastCart && lastCart.attributes && lastCart.attributes[LOG_ATTR]) || '');
      var next = ((cur ? cur + '|' : '') + tok).slice(-500);
      gapLog = next;
      var a = {}; a[LOG_ATTR] = next;
      return fetch('/cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: a })
      }).then(function (r) { if (!r.ok) throw new Error('update.js HTTP ' + r.status); });
    }).catch(function (e) { console.warn('[hbx-gap] log failed', e); })
      .then(function () { writing--; });
    return logWrite;
  }

  /* Resolve when p settles or after ms, whichever is first: a slow write never holds checkout long. */
  function settle(p, ms) {
    return Promise.race([p, new Promise(function (r) { setTimeout(r, ms || 1200); })]);
  }

  function subtotal(c) {
    if (!c) return 0;
    try { return utils ? utils.eligibleSubtotal(c, 'pre') : c.items_subtotal_price; } catch (e) { return c.items_subtotal_price || 0; }
  }

  /* First checkout tap after a "back": did the cart grow since? */
  function backCheckout() {
    var was = null;
    try { was = sessionStorage.getItem(BACK_KEY); } catch (e) {}
    if (was == null || !lastCart) return;
    try { sessionStorage.removeItem(BACK_KEY); } catch (e) {}
    var now = subtotal(lastCart);
    ga(now > Number(was) ? 'hbx_gap_back_more' : 'hbx_gap_back_same', { cart_was: Number(was) / 100, cart_now: now / 100 });
  }

  function loadPool() {
    if (pool) return Promise.resolve(pool);
    if (poolPromise) return poolPromise;
    poolPromise = fetch('/?sections=hbx-gap-pool', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data['hbx-gap-pool'];
        var doc = new DOMParser().parseFromString(html || '', 'text/html');
        var node = doc.querySelector('[data-hbx-gap-pool]');
        pool = node ? JSON.parse(node.textContent) : [];
        pool.forEach(function (p) {
          p.youth = /youth/i.test(p.t);
          p.apparel = APPAREL.test(p.ty || '') || APPAREL.test(p.t);
          p.min = p.v.reduce(function (m, v) { return Math.min(m, v.p); }, Infinity);
        });
        return pool;
      })
      .catch(function (e) { poolPromise = null; console.warn('[hbx-gap] pool load failed', e); return []; });
    return poolPromise;
  }

  /* Tier targets from the rewards bar markup (amounts in cents). */
  function tiers() {
    var bar = document.querySelector('[data-tier-bar]');
    if (!bar) return null;
    var amts = (bar.getAttribute('data-tiers') || '').split('|').filter(Boolean).map(Number);
    var labels = (bar.getAttribute('data-tier-labels') || '').split('|');
    var types = (bar.getAttribute('data-tier-types') || '').split('|');
    var si = types.indexOf('shipping');
    if (si < 0) return null;
    return {
      ship: amts[si],
      next: amts[si + 1] || null,
      nextType: types[si + 1] || '',
      nextLabel: labels[si + 1] || ''
    };
  }

  /* Where the cart stands: which tier we're chasing and the gap in cents. */
  function state(cart) {
    if (!utils || !cart || !cart.items || !cart.items.length) return null;
    if (cart.currency && cart.currency !== 'USD') return null;
    var t = tiers();
    if (!t) return null;
    var post = utils.eligibleSubtotal(cart, 'post');
    var pre = utils.eligibleSubtotal(cart, 'pre');
    // Team cart (Rob 10-22): free shipping at $100 on the whole cart, same as the bar + checkout (cart-extras teamShip).
    var team = utils.teamShip ? utils.teamShip(cart) : null;
    var shipAt = team ? Math.max(t.ship, team.amount) : t.ship;
    var shipSub = team ? team.sub : post;
    if (shipSub < shipAt) return { mode: 'ship', gap: shipAt - shipSub };
    if (t.next) {
      var basis = t.nextType === 'shipping' ? post : pre;
      if (basis < t.next) return { mode: 'next', gap: t.next - basis, label: t.nextLabel };
    }
    return null;
  }

  function leadItem(cart) {
    var excl = utils.excluded();
    var giftPid = utils.giftProductId();
    return cart.items.find(function (it) {
      return !excl[String(it.product_id)] && it.product_id !== giftPid;
    }) || null;
  }

  /* Ranked candidates: closers (price >= gap) in best-seller order, then — only
     if none close it — the nearest under the gap. */
  function candidates(cart, gap, n) {
    if (!pool || !pool.length) return [];
    var inCart = {};
    cart.items.forEach(function (it) { inCart[it.product_id] = true; });
    var excl = utils.excluded();
    var lead = leadItem(cart);
    var leadYouth = lead ? /youth/i.test(lead.product_title || '') : false;
    var ok = pool.filter(function (p) {
      if (inCart[p.id] || excl[String(p.id)] || !p.v.length) return false;
      if (p.apparel && p.youth !== leadYouth) return false; // adult buyer -> adult apparel, youth -> youth
      return true;
    });
    // Hank 10-18 review: "closes" is judged on the size the tile pre-selects, and sized apparel must
    // come in the shopper's size. Before, an M shopper $30.05 short got XXL tees ($32.95) pre-selected.
    var fit = ok.filter(sizeOk);
    var closers = fit.filter(function (p) { return pickVariant(p, gap).p >= gap; });
    if (closers.length) return closers.slice(0, n).map(function (p) { return { p: p, closes: true }; });
    return (fit.length ? fit : ok).slice()
      .sort(function (a, b) { return pickVariant(b, gap).p - pickVariant(a, gap).p; })
      .slice(0, n).map(function (p) { return { p: p, closes: false }; });
  }

  function sizeTail(v) { return String(v.t).split(' / ').pop(); }

  /* The size a tile pre-selects: the shopper's own size when this product has it in stock,
     else the first in-stock size that closes the gap, else the first in-stock size. */
  function pickVariant(p, gap) {
    var same = leadSize && p.v.find(function (v) { return sizeTail(v) === leadSize; });
    return same || p.v.find(function (v) { return v.p >= gap; }) || p.v[0];
  }

  /* Sized apparel is only offered in the shopper's size (one-size items and non-apparel always pass). */
  function sizeOk(p) {
    if (!leadSize || !p.apparel || p.v.length < 2) return true;
    return p.v.some(function (v) { return sizeTail(v) === leadSize; });
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(c) { return utils ? utils.money(c) : '$' + (c / 100).toFixed(2); }

  function tagText(st, closes) {
    if (!closes) return 'CLOSEST';
    return st.mode === 'ship' ? '+ FREE SHIPPING' : '+ ' + (st.label || 'NEXT REWARD').toUpperCase();
  }

  function tile(c, st, cls) {
    var p = c.p;
    // Pre-select the shopper's own size (see pickVariant); candidates() already judged "closes" on it.
    var pick = pickVariant(p, st.gap);
    var sel = '';
    if (p.v.length > 1 || (p.v[0] && p.v[0].t !== 'Default Title')) {
      sel = '<select class="cart-upsell__variant" data-hbx-gap-variant aria-label="' + esc(p.t) + ' size">' +
        p.v.map(function (v) {
          return '<option value="' + v.id + '"' + (v.id === pick.id ? ' selected' : '') + '>' + esc(v.t) + '</option>';
        }).join('') + '</select>';
    }
    var img = p.img ? '<img src="' + esc(p.img) + '" width="80" height="80" loading="lazy" alt="">' : '';
    return '<div class="' + cls + '" data-hbx-gap-tile>' +
      '<a href="' + esc(p.u) + '" class="cart-upsell__img" tabindex="-1" aria-hidden="true">' + img + '</a>' +
      '<div class="cart-upsell__info">' +
        '<span class="hbx-gap__tag' + (c.closes ? '' : ' hbx-gap__tag--near') + '">' + esc(tagText(st, c.closes)) + '</span>' +
        '<a href="' + esc(p.u) + '" class="cart-upsell__name">' + esc(p.t) + '</a>' +
        '<span class="cart-upsell__price">' + money(pick.p) + '</span>' +
        '<div class="cart-upsell__actions">' + sel +
          '<button type="button" class="cart-upsell__add btn" data-hbx-gap-add data-variant-id="' + pick.id + '">Add</button>' +
        '</div>' +
      '</div></div>';
  }

  var leadSize = '';
  var APPAREL_SIZE = /^Y?(XXS|XS|S|M|L|XL|XXL|XXXL|2XL|3XL)$/i;
  function sizeOf(cart) {
    var l = cart && leadItem(cart);
    var s = l && l.variant_title ? String(l.variant_title).split(' / ').pop().trim() : '';
    return APPAREL_SIZE.test(s) ? s : '';
  }

  function heading(st) {
    if (st.mode === 'ship') return 'Add one → FREE shipping 🚚';
    return 'Add one → ' + (st.label || 'next reward') + ' 🔥';
  }

  /* Called by cart-extras.js loadUpsell. true = we own the upsell row this render. */
  function handle(cart, wrap, u) {
    utils = u || utils;
    lastCart = cart;
    var st = state(cart);
    lastState = st;
    if (st) leadSize = sizeOf(cart);
    var grid = wrap.querySelector('[data-cart-upsell-grid]');
    var head = wrap.querySelector('.cart-upsell__heading');
    if (head && !head.hasAttribute('data-orig')) head.setAttribute('data-orig', head.textContent);
    if (!st || !grid) {
      if (wrap.getAttribute('data-hbx-gap') === '1') {
        wrap.removeAttribute('data-hbx-gap');
        wrap.classList.remove('hbx-gap');
        if (head) head.textContent = head.getAttribute('data-orig');
        grid.setAttribute('data-loaded-for', ''); // let the recommendations reload
        grid.innerHTML = '';
      }
      return false;
    }
    var sig = st.mode + '|' + st.gap + '|' + cart.items.map(function (i) { return i.product_id; }).sort().join(',');
    wrap.setAttribute('data-hbx-gap', '1');
    wrap.classList.add('hbx-gap');
    if (grid.getAttribute('data-loaded-for') === 'gap:' + sig && pool) return true;
    grid.setAttribute('data-loaded-for', 'gap:' + sig);
    if (head) head.textContent = heading(st);
    loadPool().then(function () {
      if (grid.getAttribute('data-loaded-for') !== 'gap:' + sig) return; // superseded
      var list = candidates(cart, st.gap, parseInt(wrap.getAttribute('data-limit'), 10) || 3);
      if (!list.length) { grid.innerHTML = ''; wrap.classList.add('is-empty'); return; }
      wrap.classList.remove('is-empty');
      grid.innerHTML = list.map(function (c) { return tile(c, st, 'cart-upsell__tile'); }).join('');
    });
    return true;
  }

  /* Add from a gap tile (drawer row or nudge sheet). */
  function addFrom(btn) {
    var t = btn.closest('[data-hbx-gap-tile]');
    var sel = t && t.querySelector('[data-hbx-gap-variant]');
    var vid = parseInt(sel ? sel.value : btn.getAttribute('data-variant-id'), 10);
    if (!vid || !utils) return Promise.resolve();
    btn.classList.add('is-loading'); btn.disabled = true;
    var failed = false;
    return utils.addToCart([{ id: vid, quantity: 1 }])
      .then(function (r) {
        if (r && r.status && Number(r.status) >= 400) { failed = true; throw new Error(r.description || r.message || r.status); }
        return utils.fetchCart();
      })
      .then(function (c) { utils.rebuildDrawer(c); })
      .catch(function (e) {
        console.warn('[hbx-gap] add failed', e);
        if (failed) { btn.textContent = 'Sold out'; btn.setAttribute('data-hbx-gap-failed', ''); }
      })
      .then(function () { btn.classList.remove('is-loading'); btn.disabled = failed; return failed ? false : vid; });
  }

  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-hbx-gap-add]');
    if (!b) return;
    e.preventDefault();
    var inSheet = !!b.closest('[data-hbx-gap-sheet]');
    var mode = lastState && lastState.mode; // read before the add re-renders the drawer
    addFrom(b).then(function (vid) {
      if (inSheet && vid !== false) closeSheet();
      if (!vid) return;
      logEvent((inSheet ? 's:' : mode === 'next' ? 'dn:' : 'd:') + vid);
      ga(inSheet ? 'hbx_gap_add_sheet' : 'hbx_gap_add_drawer', { tier: mode || '' });
    });
  });

  /* Changing a tile's size updates its price and tag (the tag is judged on the chosen size). */
  document.addEventListener('change', function (e) {
    var sel = e.target.closest && e.target.closest('[data-hbx-gap-variant]');
    if (!sel || !pool) return;
    var t = sel.closest('[data-hbx-gap-tile]');
    var v = null;
    pool.some(function (p) { v = p.v.find(function (x) { return String(x.id) === sel.value; }) || null; return !!v; });
    if (!t || !v) return;
    var price = t.querySelector('.cart-upsell__price');
    if (price) price.textContent = money(v.p);
    var btn = t.querySelector('[data-hbx-gap-add]');
    if (btn) { btn.setAttribute('data-variant-id', v.id); if (btn.hasAttribute('data-hbx-gap-failed')) { btn.removeAttribute('data-hbx-gap-failed'); btn.textContent = 'Add'; btn.disabled = false; } }
    var tag = t.querySelector('.hbx-gap__tag');
    if (tag && lastState) {
      var closes = v.p >= lastState.gap;
      tag.textContent = tagText(lastState, closes);
      tag.classList.toggle('hbx-gap__tag--near', !closes);
    }
  });

  /* ---- 2. Checkout nudge (slide-up sheet inside the drawer) ---- */
  var pendingBtn = null;

  function shown() { try { return sessionStorage.getItem(NUDGE_KEY) === '1'; } catch (e) { return true; } }
  function markShown() { try { sessionStorage.setItem(NUDGE_KEY, '1'); } catch (e) {} }

  function closeSheet() {
    var s = document.querySelector('[data-hbx-gap-sheet]');
    if (!s) return;
    s.classList.remove('is-open');
    setTimeout(function () { if (s.parentNode) s.parentNode.removeChild(s); }, 260);
  }

  function openSheet(st, list) {
    var host = document.querySelector('#CartDrawer') || document.body;
    var s = document.createElement('div');
    s.className = 'hbx-gap-sheet';
    s.setAttribute('data-hbx-gap-sheet', '');
    s.setAttribute('role', 'dialog');
    s.setAttribute('aria-label', 'Free shipping offer');
    s.innerHTML =
      '<div class="hbx-gap-sheet__scrim" data-hbx-gap-anyway></div>' +
      '<div class="hbx-gap-sheet__panel">' +
        '<button type="button" class="hbx-gap-sheet__x" data-hbx-gap-anyway aria-label="Close">×</button>' +
        '<p class="hbx-gap-sheet__title">Wait! You\'re ' + money(st.gap) + ' from FREE shipping 🚚</p>' +
        '<p class="hbx-gap-sheet__sub">Add one of these and shipping\'s on us.</p>' +
        '<div class="hbx-gap-sheet__grid">' + list.map(function (c) { return tile(c, st, 'hbx-gap-sheet__tile'); }).join('') + '</div>' +
        '<button type="button" class="btn hbx-gap-sheet__anyway" data-hbx-gap-anyway data-hbx-gap-go>Checkout anyway →</button>' +
      '</div>';
    host.appendChild(s);
    requestAnimationFrame(function () { s.classList.add('is-open'); });
  }

  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('[data-hbx-gap-anyway]');
    if (!a) return;
    e.preventDefault();
    var go = a.hasAttribute('data-hbx-gap-go');
    closeSheet();
    if (!go) {
      try { sessionStorage.setItem(BACK_KEY, String(subtotal(lastCart))); } catch (err) {}
      ga('hbx_gap_back');
    } else {
      ga('hbx_gap_go');
    }
    var w = logEvent(go ? 'go' : 'back');
    if (go && pendingBtn) {
      var b = pendingBtn; pendingBtn = null;
      // Let the log land before checkout starts (cap 1.2s), then release the real click.
      settle(w).then(function () { releasing = true; try { b.click(); } finally { releasing = false; } });
    }
  });

  // Capture phase so we run before cart-extras' Redo checkout handler. Only the
  // primary checkout buttons; the Redo opt-out link and express buttons pass straight through.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-redo-checkout], #CartDrawer button[name="checkout"]');
    if (!btn || btn.disabled) return;
    if (shown()) {
      backCheckout();
      // A log write still in flight: hold the tap until it lands (cap 1.2s) so it
      // reaches the order and never races the Redo add.
      if (writing && !releasing) {
        e.preventDefault();
        e.stopImmediatePropagation();
        releasing = true;
        settle(logWrite).then(function () { try { btn.click(); } finally { releasing = false; } });
      }
      return;
    }
    try {
      if (!pool || !lastCart) return;
      var st = state(lastCart);
      if (!st || st.mode !== 'ship') return;
      var list = candidates(lastCart, st.gap, 2).filter(function (c) { return c.closes; });
      if (!list.length) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      markShown();
      pendingBtn = btn;
      openSheet(st, list);
      logEvent('n:' + (st.gap / 100).toFixed(2));
      ga('hbx_gap_nudge', { gap: st.gap / 100 });
    } catch (err) { console.warn('[hbx-gap] nudge skipped', err); }
  }, true);

  window.HbxGap = { handle: handle, _state: function () { return lastState; }, _log: function () { return gapLog; } };
})();
